/**
 * PushNotificationManager
 * - Context로 전체 앱에 알림 상태 공유
 * - Flutter → window.dispatchEvent('mrt_push') 이벤트 수신
 * - 워크스페이스 DB (push_list 테이블) 기반으로 이력 관리
 * - 개인 삭제 기능 없음 (관리자 전용)
 */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import {
  countUnreadByCategory,
  filterNotifsByCategory,
  NOTICE_BODY_PREVIEW_STYLE,
  resolvePushTypeForSave,
} from '../utils/pushNotifCategory';

const PushContext = createContext(null);

const LEGACY_STORAGE_KEY = 'mrt_push_notifs';

function buildNotifFromDetail(detail) {
  const d = detail || {};
  const pushType = resolvePushTypeForSave(d);
  return {
    seq: d.push_seq ? Number(d.push_seq) : Date.now(),
    title: d.title || 'MRT 알림',
    body: d.body || '',
    pushType,
    extSeq: d.push_seq ? Number(d.push_seq) : null,
    receivedAt: new Date().toISOString(),
    read: false,
    pushData: {
      kind: String(d.kind || '').trim(),
      screen: String(d.screen || '').trim(),
      order_seq: String(d.order_seq || '').trim(),
      issue_no: String(d.issue_no || '').trim(),
    },
  };
}

function mapPersonalPushItem(n) {
  const title = n.title || 'MRT 공지';
  let pushType = n.pushType || 'broadcast';
  if (pushType === 'individual') {
    pushType = resolvePushTypeForSave({ title, kind: '' });
  }
  return {
    seq: n.seq,
    title,
    body: n.body || '',
    pushType,
    extSeq: n.extSeq || null,
    read: !!n.isRead,
    receivedAt: n.receivedAt,
    readAt: n.readAt,
    pushData: { kind: '' },
  };
}

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString('ko-KR'); } catch { return iso; }
}
function fmtShort(iso) {
  try {
    const d = new Date(iso);
    return `${d.getFullYear()}.${String(d.getMonth()+1).padStart(2,'0')}.${String(d.getDate()).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
  } catch { return ''; }
}
function getAuthToken() {
  try {
    const stored = JSON.parse(localStorage.getItem('user') || '{}');
    return stored?.token ? String(stored.token) : '';
  } catch { return ''; }
}
function authHeaders() {
  const t = getAuthToken();
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}

/* ── 팝업 알림 모달 (새 알림 도착 시) ─────────────────────────── */
function NewPushModal({ modal, onConfirm, onShowList }) {
  if (!modal) return null;
  return (
    <div
      onClick={() => onConfirm(modal)}
      style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: '#1a1a2e', border: '1px solid #2a2a3e', borderRadius: 16,
          maxWidth: 420, width: '100%', overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.7)' }}>
        <div style={{ background: 'linear-gradient(135deg,#6c63ff,#5a52d5)', padding: '16px 20px',
          display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 20 }}>📢</span>
          <span style={{ color: '#fff', fontWeight: 700, fontSize: 15, flex: 1 }}>{modal.title}</span>
        </div>
        <div style={{ padding: '20px 20px 0' }}>
          <p style={{ color: '#ccc', fontSize: 14, lineHeight: 1.7, whiteSpace: 'pre-wrap', margin: 0 }}>{modal.body}</p>
          <p style={{ color: '#555566', fontSize: 11, marginTop: 10, marginBottom: 0 }}>{fmtDate(modal.receivedAt)}</p>
        </div>
        <div style={{ padding: 20, display: 'flex', gap: 10 }}>
          <button onClick={() => { onConfirm(modal); onShowList(); }}
            style={{ flex: 1, padding: '10px 0', background: '#2a2a3e', border: 'none',
              borderRadius: 8, color: '#aaa', cursor: 'pointer', fontSize: 13 }}>
            목록 보기
          </button>
          <button onClick={() => onConfirm(modal)}
            style={{ flex: 2, padding: '10px 0',
              background: 'linear-gradient(135deg,#6c63ff,#5a52d5)', border: 'none',
              borderRadius: 8, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
            확인
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── 상세 내용 모달 (벨 목록/MyNotificationsPage 공용) ──────────── */
export function NotifDetailModal({ notif, onClose, onGoScreen }) {
  if (!notif) return null;
  const screen = String(notif?.pushData?.screen || '').trim();
  const goLabel = screen === 'outgoing_list' ? '출고 리스트 열기' : '';
  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.72)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: '#1a1a2e', border: '1px solid #2a2a3e', borderRadius: 16,
          maxWidth: 480, width: '100%', overflow: 'hidden',
          boxShadow: '0 24px 64px rgba(0,0,0,0.7)' }}>
        {/* 헤더 */}
        <div style={{ background: 'linear-gradient(135deg,#6c63ff,#5a52d5)',
          padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 20 }}>📢</span>
          <span style={{ color: '#fff', fontWeight: 800, fontSize: 15, flex: 1,
            wordBreak: 'break-word' }}>
            {notif.title}
          </span>
          {notif.isBroadcastOnly && (
            <span style={{ fontSize: 11, fontWeight: 700, color: '#38bdf8',
              background: 'rgba(56,189,248,0.2)', borderRadius: 6, padding: '2px 7px',
              flexShrink: 0 }}>
              전체공지
            </span>
          )}
        </div>
        {/* 본문 */}
        <div style={{ padding: '22px 22px 0' }}>
          <p style={{ color: '#d0d0e8', fontSize: 14, lineHeight: 1.85,
            whiteSpace: 'pre-wrap', margin: 0, wordBreak: 'break-word' }}>
            {notif.body || '(내용 없음)'}
          </p>
        </div>
        {/* 메타 */}
        <div style={{ padding: '14px 22px', display: 'flex', gap: 20,
          fontSize: 12, color: '#555566', flexWrap: 'wrap' }}>
          <div>
            <span style={{ color: '#8888aa' }}>수신: </span>
            <span style={{ color: '#9999bb' }}>{fmtDate(notif.receivedAt)}</span>
          </div>
          {notif.read && (
            <div>
              <span style={{ color: '#8888aa' }}>확인: </span>
              <span style={{ color: '#4ade80' }}>
                {notif.readAt ? fmtDate(notif.readAt) : '확인함'}
              </span>
            </div>
          )}
        </div>
        {/* 액션 / 닫기 */}
        <div style={{ padding: '0 22px 20px', display: 'flex', gap: 10 }}>
          {goLabel ? (
            <button
              onClick={() => onGoScreen?.(notif)}
              style={{
                flex: 2, padding: '11px 0', border: 'none', borderRadius: 10,
                background: 'linear-gradient(135deg,#6c63ff,#5a52d5)',
                color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 700,
              }}
            >
              {goLabel}
            </button>
          ) : null}
          <button onClick={onClose}
            style={{
              flex: goLabel ? 1 : undefined,
              width: goLabel ? undefined : '100%',
              padding: '11px 0',
              background: '#2a2a3e', border: 'none', borderRadius: 10,
              color: '#aaa', cursor: 'pointer', fontSize: 14, fontWeight: 600,
            }}>
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── 알림 목록 모달 (벨 클릭 시) ───────────────────────────────── */
function NotifListModal({ notifs, loading, unreadCount, onConfirmOne, onReadAll, onClose, onRefresh }) {
  const [detailItem, setDetailItem] = useState(null);
  const [categoryTab, setCategoryTab] = useState('notice');

  const openDetail = (n) => {
    setDetailItem(n);
  };

  const CATEGORY_TABS = [
    { key: 'notice', label: `공지사항${countUnreadByCategory(notifs, 'notice') ? ` (${countUnreadByCategory(notifs, 'notice')})` : ''}` },
    { key: 'approval', label: `결재${countUnreadByCategory(notifs, 'approval') ? ` (${countUnreadByCategory(notifs, 'approval')})` : ''}` },
  ];

  const isNoticeView = categoryTab === 'notice';

  const renderRows = (items) => items.map((n) => (
    <div key={n.seq}
      style={{ display: 'grid', gridTemplateColumns: '1fr 80px 76px',
        alignItems: isNoticeView ? 'start' : 'center',
        borderBottom: '1px solid #1e1e30',
        background: n.read ? 'transparent' : 'rgba(108,99,255,0.08)',
        transition: 'background 0.15s' }}>

      <div
        onClick={isNoticeView ? undefined : () => openDetail(n)}
        style={{ minWidth: 0, padding: '11px 8px 11px 16px',
          cursor: isNoticeView ? 'default' : 'pointer' }}
        onMouseEnter={isNoticeView ? undefined : (e => { e.currentTarget.style.background = 'rgba(108,99,255,0.15)'; })}
        onMouseLeave={isNoticeView ? undefined : (e => { e.currentTarget.style.background = 'transparent'; })}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: isNoticeView ? 6 : 2 }}>
          {!n.read && (
            <span style={{ width: 6, height: 6, borderRadius: '50%',
              background: '#6c63ff', flexShrink: 0, marginTop: 1 }} />
          )}
          <span style={{ fontWeight: n.read ? 500 : 700, fontSize: 13,
            color: n.read ? '#9999bb' : '#e8e8f0',
            ...(isNoticeView ? { wordBreak: 'break-word' } : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }) }}>
            {n.title}
          </span>
          {!isNoticeView && (
            <span style={{
              fontSize: 10, fontWeight: 700, flexShrink: 0, borderRadius: 4, padding: '1px 5px',
              color: '#fbbf24',
              background: 'rgba(251,191,36,0.12)',
            }}>
              결재
            </span>
          )}
        </div>
        <div style={{
          fontSize: 12,
          color: isNoticeView ? '#8888aa' : '#666688',
          paddingLeft: n.read ? 0 : 12,
          ...(isNoticeView
            ? NOTICE_BODY_PREVIEW_STYLE
            : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }),
        }}>
          {n.body || (isNoticeView ? '(내용 없음)' : '')}
        </div>
      </div>

      <div style={{ fontSize: 11, color: '#8888aa', textAlign: 'center', padding: isNoticeView ? '11px 4px 0' : '0 4px' }}>
        {fmtShort(n.receivedAt)}
      </div>

      <div style={{ textAlign: 'center', padding: isNoticeView ? '11px 4px 0' : '0 4px' }}>
        {n.read ? (
          <span style={{ fontSize: 11, color: '#4ade80' }}>확인함</span>
        ) : (
          <button
            onClick={() => onConfirmOne(n)}
            style={{ fontSize: 11, fontWeight: 700, color: '#fff',
              background: 'linear-gradient(135deg,#6c63ff,#5a52d5)',
              border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer' }}>
            확인
          </button>
        )}
      </div>
    </div>
  ));

  const renderListBody = () => {
    if (loading) {
      return (
        <div style={{ padding: '40px 16px', textAlign: 'center', color: '#8888aa', fontSize: 13 }}>
          불러오는 중…
        </div>
      );
    }

    const filtered = filterNotifsByCategory(notifs, categoryTab);
    if (filtered.length === 0) {
      return (
        <div style={{ padding: '40px 16px', textAlign: 'center', color: '#8888aa', fontSize: 13 }}>
          {categoryTab === 'approval' ? '결재 알림이 없습니다' : '공지사항이 없습니다'}
        </div>
      );
    }
    return renderRows(filtered);
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, zIndex: 9100, background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
        <div
          onClick={e => e.stopPropagation()}
          style={{ background: '#1a1a2e', border: '1px solid #2a2a3e', borderRadius: 14,
            width: '100%', maxWidth: 560, maxHeight: '80vh',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
            boxShadow: '0 16px 48px rgba(0,0,0,0.6)' }}>

          {/* 헤더 */}
          <div style={{ padding: '14px 18px', borderBottom: '1px solid #2a2a3e',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: 'linear-gradient(135deg,#1a1a2e,#1e1e38)', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 18 }}>🔔</span>
              <span style={{ fontWeight: 800, fontSize: 15, color: '#e8e8f0' }}>알림 목록</span>
              {unreadCount > 0 && (
                <span style={{ background: '#ef4444', color: '#fff', borderRadius: 10,
                  fontSize: 11, fontWeight: 700, padding: '2px 7px' }}>
                  {unreadCount}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {notifs.some(n => !n.read) && (
                <button onClick={onReadAll}
                  style={{ background: 'rgba(108,99,255,0.2)', border: '1px solid rgba(108,99,255,0.4)',
                    color: '#a09af8', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    borderRadius: 6, padding: '4px 10px' }}>
                  전체 읽음
                </button>
              )}
              <button onClick={onRefresh}
                style={{ background: 'none', border: 'none', color: '#8888aa', cursor: 'pointer', fontSize: 13 }}>
                🔄
              </button>
              <button onClick={onClose}
                style={{ background: 'none', border: 'none', color: '#8888aa', cursor: 'pointer',
                  fontSize: 18, lineHeight: 1, padding: '0 4px' }}>
                ✕
              </button>
            </div>
          </div>

          {/* 카테고리 탭 */}
          <div style={{ display: 'flex', gap: 6, padding: '10px 16px', background: '#12121f',
            borderBottom: '1px solid #2a2a3e', flexShrink: 0 }}>
            {CATEGORY_TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setCategoryTab(tab.key)}
                style={{
                  padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  border: categoryTab === tab.key ? '1px solid #6c63ff' : '1px solid #2a2a3e',
                  background: categoryTab === tab.key ? 'linear-gradient(135deg,#6c63ff,#5a52d5)' : 'transparent',
                  color: categoryTab === tab.key ? '#fff' : '#8888aa',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* 컬럼 헤더 */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 76px',
            padding: '6px 16px', background: '#12121f', flexShrink: 0,
            borderBottom: '1px solid #2a2a3e' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#555566',
              textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {isNoticeView ? '제목 · 내용 (3줄)' : '내용 (클릭하면 전체 보기)'}
            </span>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#555566', textAlign: 'center' }}>수신일시</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#555566', textAlign: 'center' }}>확인</span>
          </div>

          {/* 목록 */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {renderListBody()}
          </div>

          {/* 하단 */}
          <div style={{ padding: '10px 18px', borderTop: '1px solid #2a2a3e', flexShrink: 0,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            background: '#12121f' }}>
            <span style={{ fontSize: 12, color: '#555566' }}>
              총 {notifs.length}개 · 미확인 {unreadCount}개
            </span>
            <button onClick={onClose}
              style={{ background: '#2a2a3e', border: 'none', borderRadius: 8,
                color: '#aaa', cursor: 'pointer', fontSize: 13, padding: '6px 18px' }}>
              닫기
            </button>
          </div>
        </div>
      </div>

      {/* 상세 모달 (내용 전체 보기) */}
      {detailItem && (
        <NotifDetailModal
          notif={detailItem}
          onClose={() => setDetailItem(null)}
        />
      )}
    </>
  );
}

/* ── 전체공지 + 개인알림 합치기 ─────────────────────────────────── */
function mergeNotifs(personal, broadcasts) {
  // personal 에 이미 연결된 extSeq(erp_tweets.seq) 집합
  const usedExtSeqs = new Set(personal.map(n => n.extSeq).filter(Boolean));
  const broadcastItems = broadcasts
    .filter(b => !usedExtSeqs.has(b.seq))
    .map(b => ({
      seq: `b_${b.seq}`,
      extSeq: b.seq,
      title: b.title || 'MRT 공지',
      body: b.body || '',
      pushType: 'broadcast',
      read: !!b.isRead,
      receivedAt: b.createdAt,
      readAt: b.readAt || null,
      isBroadcastOnly: true,
    }));
  const all = [...personal, ...broadcastItems];
  all.sort((a, b2) => new Date(b2.receivedAt || 0) - new Date(a.receivedAt || 0));
  return all;
}

/* ── Provider ───────────────────────────────────────────────────── */
export function PushNotificationProvider({ children }) {
  const apiUrl = getApiBaseUrl();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [modal, setModal] = useState(null);      // 새 알림 팝업
  const [openedDetail, setOpenedDetail] = useState(null); // 상태바 탭 → 즉시 상세
  const [showPanel, setShowPanel] = useState(false); // 목록 모달
  const [loading, setLoading] = useState(false);
  const notifsRef = useRef(notifs);
  notifsRef.current = notifs;

  // ── DB에서 목록 로드 (개인 수신함 + 전체공지 합치기) ──────────
  const fetchNotifs = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      setLoading(true);
      const [resPersonal, resBroadcast] = await Promise.all([
        fetch(`${apiUrl}/api/push/my?limit=100`, { headers: authHeaders() }),
        fetch(`${apiUrl}/api/erp/push/my-broadcasts?limit=100`, { headers: authHeaders() }),
      ]);
      const personalData = resPersonal.ok ? await resPersonal.json() : { items: [] };
      const broadcastData = resBroadcast.ok ? await resBroadcast.json() : { items: [] };

      const personal = (personalData.items || []).map(mapPersonalPushItem);
      const merged = mergeNotifs(personal, broadcastData.items || []);
      setNotifs(merged);
      setUnreadCount(merged.filter(n => !n.read).length);
    } catch {
      try {
        const legacy = JSON.parse(localStorage.getItem(LEGACY_STORAGE_KEY) || '[]');
        if (legacy.length > 0) setNotifs(legacy);
      } catch { /* ignore */ }
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => { fetchNotifs(); }, [fetchNotifs]);

  // ── DB에 수신 저장 ────────────────────────────────────────────
  const saveToDb = useCallback(async (title, body, extSeq, pushType = 'broadcast', senderEmail = null) => {
    const token = getAuthToken();
    if (!token) return null;
    try {
      const res = await fetch(`${apiUrl}/api/push/receive`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ title, body, ext_seq: extSeq, push_type: pushType, sender_email: senderEmail }),
      });
      if (res.ok) {
        const d = await res.json();
        return d.seq || null;
      }
    } catch { /* ignore */ }
    return null;
  }, [apiUrl]);

  // ── 읽음 처리 ─────────────────────────────────────────────────
  const markRead = useCallback(async (seq) => {
    if (!seq) return;
    try {
      await fetch(`${apiUrl}/api/push/read`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ seq }),
      });
    } catch { /* ignore */ }
  }, [apiUrl]);

  const markReadErp = useCallback(async (extSeq) => {
    if (!extSeq) return;
    try {
      await fetch(`${apiUrl}/api/erp/push/read`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ push_seq: extSeq }),
      });
    } catch { /* ignore */ }
  }, [apiUrl]);

  // ── 단건 확인 (읽음 처리) ─────────────────────────────────────
  const confirmNotif = useCallback((notif) => {
    if (!notif.read) {
      setNotifs(prev => prev.map(n => n.seq === notif.seq ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
      // isBroadcastOnly: push_list 없이 erp_tweets만 있는 전체공지 → ERP 읽음만
      if (notif.isBroadcastOnly) {
        if (notif.extSeq) markReadErp(notif.extSeq);
      } else {
        markRead(notif.seq);
        if (notif.extSeq) markReadErp(notif.extSeq);
      }
    }
  }, [markRead, markReadErp]);

  // ── 팝업 닫기 + 읽음 ─────────────────────────────────────────
  const handleConfirm = useCallback((notif) => {
    setModal(null);
    confirmNotif(notif);
  }, [confirmNotif]);

  // ── 목록에서 단건 확인 ────────────────────────────────────────
  const handleConfirmOne = useCallback((notif) => {
    confirmNotif(notif);
  }, [confirmNotif]);

  // ── 전체 읽음 ─────────────────────────────────────────────────
  const readAll = useCallback(async () => {
    // push_list 전체 읽음
    try {
      await fetch(`${apiUrl}/api/push/read_all`, { method: 'POST', headers: authHeaders() });
    } catch { /* ignore */ }
    // broadcast-only(erp_tweets) 미읽음 항목도 ERP에 읽음 처리
    const unreadBroadcasts = notifsRef.current.filter(n => !n.read && n.isBroadcastOnly && n.extSeq);
    await Promise.all(unreadBroadcasts.map(n =>
      fetch(`${apiUrl}/api/erp/push/read`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ push_seq: n.extSeq }),
      }).catch(() => {})
    ));
    setNotifs(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  }, [apiUrl]);

  // ── 새 푸시 추가 (Flutter 이벤트) ────────────────────────────
  const ingestPush = useCallback(async (detail, { showPopup = false, showDetail = false } = {}) => {
    const n = buildNotifFromDetail(detail);
    const seq = await saveToDb(
      n.title,
      n.body,
      n.extSeq ? Number(n.extSeq) : null,
      n.pushType,
    );
    if (seq) n.seq = seq;
    setNotifs(prev => [n, ...prev]);
    setUnreadCount(prev => prev + 1);
    if (showPopup) setModal(n);
    if (showDetail) setOpenedDetail(n);
    return n;
  }, [saveToDb]);

  const handleGoScreen = useCallback((notif) => {
    const screen = String(notif?.pushData?.screen || '').trim();
    const orderSeq = String(notif?.pushData?.order_seq || '').trim();
    let target = '';
    if (screen === 'outgoing_list') {
      target = orderSeq
        ? `/erp?menu=outgoing_list&order_seq=${encodeURIComponent(orderSeq)}`
        : '/erp?menu=outgoing_list';
    }
    setOpenedDetail(null);
    confirmNotif(notif);
    if (!target) return;
    if (getAuthToken()) {
      navigate(target);
      return;
    }
    navigate('/login', { state: { from: target } });
  }, [navigate, confirmNotif]);

  const closeOpenedDetail = useCallback((notif) => {
    setOpenedDetail(null);
    if (notif) confirmNotif(notif);
  }, [confirmNotif]);

  // ── Flutter 이벤트 수신 ───────────────────────────────────────
  useEffect(() => {
    const onPush = (e) => {
      ingestPush(e.detail || {}, { showPopup: true });
    };
    const onPushOpen = (e) => {
      // 상태바 탭: 로그인 없이도 FCM payload(title/body/data)로 상세 모달 즉시 표시
      ingestPush(e.detail || {}, { showDetail: true });
    };
    window.addEventListener('mrt_push', onPush);
    window.addEventListener('mrt_push_open', onPushOpen);
    window.testPush = (title = '테스트 공지', body = '테스트 메시지입니다') =>
      window.dispatchEvent(new CustomEvent('mrt_push', { detail: { title, body, push_seq: null } }));
    window.testPushOpen = (title = '출고 요청', body = '[ORD-001] 프로젝트A · 시리얼 3건') =>
      window.dispatchEvent(new CustomEvent('mrt_push_open', {
        detail: {
          title,
          body,
          push_seq: null,
          kind: 'outgoing',
          screen: 'outgoing_list',
          order_seq: '123',
          issue_no: 'ORD-001',
        },
      }));
    return () => {
      window.removeEventListener('mrt_push', onPush);
      window.removeEventListener('mrt_push_open', onPushOpen);
    };
  }, [ingestPush]);

  return (
    <PushContext.Provider value={{ unreadCount, showPanel, setShowPanel, notifs, confirmNotif, readAll, fetchNotifs, loading }}>
      {children}

      {/* 목록 모달 (벨 클릭) */}
      {showPanel && (
        <NotifListModal
          notifs={notifs}
          loading={loading}
          unreadCount={unreadCount}
          onConfirmOne={handleConfirmOne}
          onReadAll={readAll}
          onClose={() => setShowPanel(false)}
          onRefresh={fetchNotifs}
        />
      )}

      {/* 새 알림 팝업 모달 */}
      <NewPushModal
        modal={modal}
        onConfirm={handleConfirm}
        onShowList={() => setShowPanel(true)}
      />

      {/* 상태바 푸시 탭 → 로그인 없이 본문 확인 */}
      <NotifDetailModal
        notif={openedDetail}
        onClose={() => closeOpenedDetail(openedDetail)}
        onGoScreen={handleGoScreen}
      />
    </PushContext.Provider>
  );
}

// Header에서 사용할 벨 아이콘 컴포넌트
export function PushBellButton() {
  const ctx = useContext(PushContext);
  if (!ctx) return null;
  const { unreadCount, setShowPanel, fetchNotifs } = ctx;
  return (
    <button
      onClick={() => setShowPanel(v => {
        if (!v) fetchNotifs(); // 패널 열 때 최신 목록 조회
        return !v;
      })}
      title="알림"
      style={{
        position: 'relative',
        background: 'rgba(255,255,255,0.15)',
        border: '1.5px solid rgba(255,255,255,0.45)',
        borderRadius: '50%',
        width: 32, height: 32,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', flexShrink: 0,
      }}
    >
      <span style={{ fontSize: 16, lineHeight: 1 }}>🔔</span>
      {unreadCount > 0 && (
        <span style={{
          position: 'absolute', top: -3, right: -3,
          background: '#ef4444', color: '#fff',
          borderRadius: '50%', fontSize: 10, fontWeight: 700,
          minWidth: 16, height: 16,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '0 3px', lineHeight: 1,
        }}>
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
    </button>
  );
}

export default PushNotificationProvider;
